import { useEffect, useMemo } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  Content,
  ContentHeader,
  DocsIcon,
  SupportButton,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogFilterLayout,
  DefaultEntityFilters,
  EntityFilter,
  EntityKindPicker,
  EntityListProvider,
  EntitySearchBar,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import Button from '@material-ui/core/Button';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_WORKFLOW_OWNER,
  getWorkflowOwner,
  workflowOwnerTitle,
} from './workflowOwners';
import { WorkflowTagPicker } from './WorkflowTagPicker';
import { getWorkflowGroups } from './workflowGroups';
import type { WorkflowGroup } from './workflowGroups';
import { CollapsibleWorkflowGroups } from './CollapsibleWorkflowGroups';

type Props = {
  configuredGroups?: WorkflowGroup[];
  templateFilter?: (entity: Entity) => boolean;
};

function WorkflowListContent({
  owner,
  configuredGroups,
  templateFilter,
}: Props & { owner: string }) {
  const navigate = useNavigate();
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const registerRoute = useRouteRef(
    scaffolderPlugin.externalRoutes.registerComponent,
  );
  const viewTechDocsRoute = useRouteRef(
    scaffolderPlugin.externalRoutes.viewTechDoc,
  );
  const { allowed: canRegister } = usePermission({
    permission: catalogEntityCreatePermission,
  });
  const { backendEntities, updateFilters } = useEntityList<
    DefaultEntityFilters & { workflowOwner?: EntityFilter }
  >();
  const ownerFilter = useMemo(
    () => ({
      filterEntity: (entity: Entity) =>
        getWorkflowOwner(entity) === owner &&
        (!templateFilter || templateFilter(entity)),
    }),
    [owner, templateFilter],
  );
  const groups = useMemo(
    () =>
      getWorkflowGroups(
        backendEntities.filter(ownerFilter.filterEntity),
        configuredGroups,
      ),
    [backendEntities, ownerFilter, configuredGroups],
  );

  useEffect(() => {
    updateFilters({ workflowOwner: ownerFilter });
  }, [ownerFilter, updateFilters]);

  return (
    <Content>
      <ContentHeader title={`Workflows - ${workflowOwnerTitle(owner)}`}>
        {registerRoute && canRegister && (
          <Button
            component={Link}
            to={registerRoute()}
            variant="outlined"
            color="primary"
          >
            Register Existing Component
          </Button>
        )}
        <SupportButton>Choose a workflow to get started.</SupportButton>
      </ContentHeader>
      <CatalogFilterLayout>
        <CatalogFilterLayout.Filters>
          <EntitySearchBar />
          <EntityKindPicker initialFilter="template" hidden />
          <UserListPicker
            initialFilter="all"
            availableFilters={['all', 'starred']}
          />
          <WorkflowTagPicker filterEntity={ownerFilter.filterEntity} />
        </CatalogFilterLayout.Filters>
        <CatalogFilterLayout.Content>
          <CollapsibleWorkflowGroups
            groups={groups}
            templateFilter={ownerFilter.filterEntity}
            additionalLinksForEntity={template => {
              if (
                !viewTechDocsRoute ||
                !(
                  template.metadata.annotations?.[
                    'backstage.io/techdocs-ref'
                  ] ||
                  template.metadata.annotations?.[
                    'backstage.io/techdocs-entity'
                  ]
                )
              )
                return [];
              const url = buildTechDocsURL(template, viewTechDocsRoute);
              return url
                ? [{ icon: DocsIcon, text: 'View TechDocs', url }]
                : [];
            }}
            onTemplateSelected={template =>
              navigate(
                templateRoute({
                  namespace: template.metadata.namespace ?? 'default',
                  templateName: template.metadata.name,
                }),
              )
            }
          />
        </CatalogFilterLayout.Content>
      </CatalogFilterLayout>
    </Content>
  );
}

export function OwnerWorkflowList(props: Props) {
  const [searchParams] = useSearchParams();
  const owner =
    searchParams.get('owner')?.toLowerCase() || DEFAULT_WORKFLOW_OWNER;
  return (
    <EntityListProvider key={owner}>
      <WorkflowListContent {...props} owner={owner} />
    </EntityListProvider>
  );
}
